import { useEffect, useState } from "react";
import _uniqBy from "lodash/uniqBy";
import _uniq from "lodash/uniq";
import { useQuery } from "react-query";
import { billingService } from "../services";
import {
  IPrice,
  ICouponObject,
  IAddOns,
  IRecurlyError,
  ISummaryDiscount,
} from "../types";

const useCheckoutPreview = (
  planId: string | undefined,
  discounts: ISummaryDiscount[] = [],
  addOns: IAddOns[] = [],
  countryCode?: string
) => {
  const [userCouponCodes, setUserCouponCode] = useState<ICouponObject[]>([]);
  const [price, setPrice] = useState<IPrice>();
  const [couponErrors, setCouponErrors] = useState<string[]>([]);
  const [isCheckoutPreviewRequestLoading, setIsCheckoutPreviewRequestLoading] =
    useState(false);
  const hasAccountDiscount = !!discounts.length;

  const addOnsCodes = addOns.map(({ code }) => code);

  const fetchPlanPreview = async (
    planId: string | undefined,
    addOnsCodes: string[],
    userCouponCodes: ICouponObject[],
    countryCode?: string
  ): Promise<IPrice> => {
    if (!planId) throw new Error("Plan ID is required");

    const couponCodes = userCouponCodes.map(({ couponCode }) => couponCode);

    const result = await billingService.checkoutPreview({
      planId,
      couponCodes,
      addOns: addOnsCodes,
      countryCode,
    });

    if (!result?.total || !result?.amount || !result?.tax) {
      throw new Error("Invalid response from checkout preview");
    }

    return result;
  };

  const { isLoading: queryLoading, refetch: refetchPrices } = useQuery(
    ["planPreview", planId, addOnsCodes, userCouponCodes, countryCode],
    () => fetchPlanPreview(planId, addOnsCodes, userCouponCodes, countryCode),
    {
      enabled: !!planId,
      onSuccess: (data) => {
        setPrice(data);
      },
    }
  );

  useEffect(() => {
    setCouponErrors([]);
    setPrice(undefined);

    refetchPrices();
  }, [planId]);

  useEffect(() => {
    const preDefinedCoupons: ICouponObject[] = discounts.map(
      (discount): ICouponObject => ({
        couponCode: discount.code,
        couponName: discount.name,
        isFreeTrial: false,
        freeTrialDescription: "",
        isRemovable: discount?.isRemovable,
      })
    );
    setUserCouponCode(preDefinedCoupons);
  }, [discounts]);

  const clearErrors = () => setCouponErrors([]);

  const setCoupon = async (couponCode: string) => {
    clearErrors();
    setIsCheckoutPreviewRequestLoading(true);

    const normalisedCoupon = {
      couponCode,
      isFreeTrial: false,
      freeTrialDescription: "",
    };

    // do not send Disability coupon code to recurly's preview.
    // Disability coupon code is only for account level discount.
    const uniqCouponCodes = _uniqBy(
      [...userCouponCodes, normalisedCoupon],
      "couponCode"
    );

    try {
      const result = await billingService.checkoutPreview({
        planId: planId || "",
        couponCodes: uniqCouponCodes.map(({ couponCode }) => couponCode),
        addOns: addOnsCodes,
        countryCode,
      });

      if (result?.total && result?.amount && result?.tax) {
        setPrice(result);
        const uniqCouponCodes = _uniqBy([...userCouponCodes], "couponCode");
        setUserCouponCode([
          ...uniqCouponCodes,
          {
            couponCode,
            isRemovable: true,
            isFreeTrial: result?.isFreeTrial || false,
            freeTrialDescription: result?.freeTrialDescription || "",
          },
        ]);
      }
    } catch (e) {
      const { response: error } = e as { response: IRecurlyError };

      // to handle 400s
      if (error?.data?.RecurlyError) {
        return setCouponErrors(
          _uniq(
            error?.data?.RecurlyError.params.map(
              ({ message }) => message ?? error?.data?.RecurlyError?.message
            )
          )
        );
      }

      // to handle 500s
      if (error?.RecurlyError) {
        return setCouponErrors(
          _uniq(
            error?.RecurlyError.params.map(
              ({ message }) => message ?? error.RecurlyError?.message
            )
          )
        );
      }
    } finally {
      setIsCheckoutPreviewRequestLoading(false);
    }
  };

  const removeCoupon = async (couponCode: string) => {
    clearErrors();

    const couponCodes = userCouponCodes.filter(
      (coupon) => coupon.couponCode !== couponCode
    );

    setIsCheckoutPreviewRequestLoading(true);

    const result = await billingService.checkoutPreview({
      planId: planId || "",
      // do not send Disability coupon code to recurly's preview.
      // Disability coupon code is only for account level discount.
      couponCodes: couponCodes.map(({ couponCode }) => couponCode),
      addOns: addOnsCodes,
      countryCode,
    });

    setIsCheckoutPreviewRequestLoading(false);

    if (result.total && result.amount && result.tax) {
      setPrice(result);
      setUserCouponCode(couponCodes);
    }
  };

  return {
    userCouponCodesObject: userCouponCodes, // for OrderSummary.tsx to show coupon code etc
    userCouponCodesStrings: userCouponCodes.map(({ couponCode }) => couponCode), // for anwhere else to use coupon code as string
    price,
    hasAccountDiscount,
    setCoupon,
    removeCoupon,
    couponErrors,
    isCheckoutPreviewRequestLoading:
      isCheckoutPreviewRequestLoading || queryLoading,
    refetchPrices,
  };
};

export default useCheckoutPreview;
